import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { housingAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import MapComponent from '../components/MapComponent';

const HousingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user: authUser } = useAuth();
  const [housing, setHousing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    fetchHousing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchHousing = async () => {
    try {
      const response = await housingAPI.getById(id);
      setHousing(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load housing details');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-on-surface-variant font-medium tracking-tight">Curating the experience...</p>
        </div>
      </div>
    );
  }

  if (error || !housing) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="bg-error-container text-on-error-container p-8 rounded-3xl border border-error/10">
          <h2 className="text-2xl font-bold mb-2">Notice</h2>
          <p>{error || 'This curated space could not be found.'}</p>
          <button onClick={() => navigate('/housing')} className="mt-6 font-bold underline">Return to listings</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen">
      <main className="max-w-[1440px] mx-auto px-6 md:px-12 pt-28 pb-24">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-8 text-on-surface-variant font-medium text-sm">
          <Link to="/housing" className="hover:text-primary transition-colors cursor-pointer">Listings</Link>
          <span className="material-symbols-outlined text-xs">chevron_right</span>
          <span className="hover:text-primary transition-colors cursor-pointer">{housing.housing_type}</span>
          <span className="material-symbols-outlined text-xs">chevron_right</span>
          <span className="text-on-surface">{housing.title}</span>
        </div>

        {/* Asymmetric Gallery */}
        <section className="grid grid-cols-12 gap-4 mb-12 h-[600px]">
          <div className="col-span-12 md:col-span-8 h-full rounded-3xl overflow-hidden relative group">
            <img 
              alt={housing.title} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
              src={housing.image_urls?.[0] || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&q=80&w=1200'}
            />
            <div className="absolute top-6 left-6 bg-primary text-on-primary px-4 py-2 rounded-full flex items-center gap-2 font-bold text-sm shadow-xl">
              <span className="material-symbols-outlined text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>stars</span>
              Curator's Choice
            </div>
          </div>
          <div className="hidden md:flex md:col-span-4 flex-col gap-4 h-full">
            <div className="h-1/2 rounded-3xl overflow-hidden group">
              <img 
                alt="Interior view" 
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                src={housing.image_urls?.[1] || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&q=80&w=800'}
              />
            </div>
            <div className="h-1/2 rounded-3xl overflow-hidden group relative">
              <img 
                alt="Kitchen view" 
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                src={housing.image_urls?.[2] || 'https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&q=80&w=800'}
              />
              <button className="absolute inset-0 bg-black/40 flex items-center justify-center text-white font-bold text-lg opacity-0 group-hover:opacity-100 transition-opacity">
                View All Photos ({housing.image_urls?.length || 1})
              </button>
            </div>
          </div>
        </section>

        {/* Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Main Column */}
          <div className="md:col-span-8">
            {/* Title and Stats Block */}
            <div className="bg-surface-container-lowest p-8 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-8 border border-outline-variant/10">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-on-surface mb-2">{housing.title}</h1>
                  <p className="text-on-surface-variant flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">location_on</span>
                    {housing.location_address}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-extrabold text-primary">
                    ${housing.price} <span className="text-lg font-medium text-on-surface-variant">/ month</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 py-6 border-y border-outline-variant/20">
                <div className="flex flex-col items-center gap-1 border-r border-outline-variant/20">
                  <span className="material-symbols-outlined text-primary-container">bed</span>
                  <span className="font-bold text-xl">{housing.bedrooms}</span>
                  <span className="text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Bedrooms</span>
                </div>
                <div className="flex flex-col items-center gap-1 border-r border-outline-variant/20">
                  <span className="material-symbols-outlined text-primary-container">bathtub</span>
                  <span className="font-bold text-xl">{housing.bathrooms || 1}</span>
                  <span className="text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Bathrooms</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <span className="material-symbols-outlined text-primary-container">square_foot</span>
                  <span className="font-bold text-xl">{housing.area_sqm}</span>
                  <span className="text-xs font-semibold text-on-surface-variant uppercase tracking-wider">Sq. Ft.</span>
                </div>
              </div>
            </div>

            {/* Description */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">The Editorial Take</h2>
              <p className="text-on-surface-variant leading-relaxed text-lg whitespace-pre-wrap">
                {housing.description}
              </p>
            </section>

            {/* Lifestyle Needs (Amenities) */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Lifestyle Needs</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {(housing.amenities && housing.amenities.length > 0) ? (
                  housing.amenities.map((amenity, idx) => (
                    <div key={idx} className="bg-surface-container-low p-6 rounded-2xl flex flex-col items-center gap-3 text-center hover:bg-primary-fixed transition-colors border border-outline-variant/5">
                      <span className="material-symbols-outlined text-primary text-3xl">
                        {amenity.toLowerCase().includes('pet') ? 'pets' : 
                         amenity.toLowerCase().includes('solar') ? 'solar_power' :
                         amenity.toLowerCase().includes('ceiling') ? 'height' :
                         amenity.toLowerCase().includes('smart') ? 'smart_toy' : 'check_circle'}
                      </span>
                      <span className="font-semibold text-sm">{amenity}</span>
                    </div>
                  ))
                ) : (
                  <>
                    <div className="bg-surface-container-low p-6 rounded-2xl flex flex-col items-center gap-3 text-center hover:bg-primary-fixed transition-colors border border-outline-variant/5">
                      <span className="material-symbols-outlined text-primary text-3xl">pets</span>
                      <span className="font-semibold text-sm">Pet Friendly</span>
                    </div>
                    <div className="bg-surface-container-low p-6 rounded-2xl flex flex-col items-center gap-3 text-center hover:bg-primary-fixed transition-colors border border-outline-variant/5">
                      <span className="material-symbols-outlined text-primary text-3xl">solar_power</span>
                      <span className="font-semibold text-sm">Solar Ready</span>
                    </div>
                    <div className="bg-surface-container-low p-6 rounded-2xl flex flex-col items-center gap-3 text-center hover:bg-primary-fixed transition-colors border border-outline-variant/5">
                      <span className="material-symbols-outlined text-primary text-3xl">height</span>
                      <span className="font-semibold text-sm">High Ceilings</span>
                    </div>
                    <div className="bg-surface-container-low p-6 rounded-2xl flex flex-col items-center gap-3 text-center hover:bg-primary-fixed transition-colors border border-outline-variant/5">
                      <span className="material-symbols-outlined text-primary text-3xl">smart_toy</span>
                      <span className="font-semibold text-sm">Smart Home</span>
                    </div>
                  </>
                )}
              </div>
            </section>

            {/* Neighborhood Insights (Map) */}
            {housing.location_lat && housing.location_lng && (
              <section>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">Neighborhood Insights</h2>
                  <button className="text-primary font-bold hover:underline">Explore District</button>
                </div>
                <div className="mb-8">
                  <MapComponent
                    latitude={parseFloat(housing.location_lat)}
                    longitude={parseFloat(housing.location_lng)}
                    locationName={housing.location_address}
                  />
                </div>
                <div className="bg-white/90 backdrop-blur-md p-4 rounded-2xl shadow-lg border border-outline-variant/10 flex items-center gap-4 -mt-12 relative z-10 mx-4">
                  <div className="bg-emerald-100 p-2 rounded-xl">
                    <span className="material-symbols-outlined text-primary">eco</span>
                  </div>
                  <div>
                    <p className="font-bold text-sm">Local Advisor Vibe</p>
                    <p className="text-xs text-on-surface-variant">This area is highly curated for walkability and local roasteries.</p>
                  </div>
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <aside className="md:col-span-4">
            <div className="sticky top-28 flex flex-col gap-6">
              {/* Inquire Card */}
              <div className="bg-surface-container-lowest p-8 rounded-3xl shadow-[0_20px_50px_rgba(0,105,72,0.1)] border border-primary-fixed/20">
                <h3 className="text-xl font-bold mb-6">Experience the Space</h3>
                
                {isAuthenticated ? (
                  <form className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-2 ml-1">Full Name</label>
                      <input 
                        className="w-full bg-surface-container-low border-none rounded-xl p-4 focus:ring-2 focus:ring-primary/20 text-sm" 
                        defaultValue={authUser?.full_name} 
                        type="text"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-2 ml-1">Preferred Date</label>
                      <input className="w-full bg-surface-container-low border-none rounded-xl p-4 focus:ring-2 focus:ring-primary/20 text-sm" type="date"/>
                    </div>
                    <button 
                      className="w-full bg-primary text-on-primary font-bold py-4 rounded-full shadow-lg shadow-primary/20 hover:bg-primary-container transition-all active:scale-95" 
                      type="submit"
                      onClick={(e) => { e.preventDefault(); alert('Inquiry sent! A curator will contact you shortly.'); }}
                    >
                      Inquire Now
                    </button>
                  </form>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-on-surface-variant text-sm mb-6">Please sign in to inquire about this space and connect with the curator.</p>
                    <button 
                      onClick={() => navigate('/')} 
                      className="w-full bg-primary text-on-primary font-bold py-4 rounded-full shadow-lg shadow-primary/20 hover:bg-primary-container transition-all"
                    >
                      Join to Inquire
                    </button>
                  </div>
                )}
                <p className="text-center text-[11px] text-on-surface-variant mt-6 uppercase tracking-widest font-semibold">Average Response Time: 15 mins</p>
              </div>

              {/* Advisor Card */}
              <div className="bg-emerald-900 text-white p-8 rounded-3xl relative overflow-hidden">
                <div className="relative z-10">
                  <h3 className="text-emerald-200 text-xs font-bold uppercase tracking-widest mb-4">Local Advisor</h3>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-emerald-800 flex items-center justify-center border-2 border-emerald-400/30 overflow-hidden">
                      <span className="material-symbols-outlined text-3xl">person</span>
                    </div>
                    <div>
                      <p className="font-bold text-lg">{housing.full_name || 'Marcus Chen'}</p>
                      <p className="text-emerald-300 text-sm">Neighborhood Expert</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3">
                    <button className="w-full bg-white/10 hover:bg-white/20 transition-colors py-3 rounded-xl flex items-center justify-center gap-2 font-semibold">
                      <span className="material-symbols-outlined text-lg">call</span>
                      Call Advisor
                    </button>
                    <button className="w-full bg-white/10 hover:bg-white/20 transition-colors py-3 rounded-xl flex items-center justify-center gap-2 font-semibold">
                      <span className="material-symbols-outlined text-lg">chat</span>
                      Message Advisor
                    </button>
                  </div>
                </div>
                <div className="absolute -right-12 -bottom-12 w-48 h-48 bg-emerald-800 rounded-full opacity-50 blur-3xl"></div>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default HousingDetailPage;
